import { safeProperties } from './measurement_core.mjs'

export const eventUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const standards = ['PageView', 'ViewContent', 'InitiateCheckout', 'AddPaymentInfo', 'CompleteRegistration', 'Purchase', 'Lead']

// Meta receives a smaller, explicit marketing vocabulary. Detailed form states
// and internal credit/loyalty accounting stay in first-party reporting.
export function metaMessage(manifest, event) {
  const definition = manifest.events[event.event_name]
  const safe = safeProperties(manifest, event.event_name, event.properties, event.source || 'browser')
  if (!definition?.meta || !eventUUID.test(event.event_id) || !safe) return null
  const properties = Object.fromEntries(Object.entries(safe).filter(([key]) =>
    ['screen_name', 'value', 'currency', 'order_type', 'package_count', 'method', 'placement', 'action', 'section_id', 'service_id', 'result'].includes(key)))
  if (event.event_name === 'purchase') {
    properties.content_type = 'product'
    properties.content_ids = ['mymalls_' + event.properties.order_type]
    properties.num_items = 1
  }
  return { event_id: event.event_id, event_name: event.event_name, source: event.source || 'browser', properties: safe,
    command: standards.includes(definition.meta) ? 'track' : 'trackCustom', name: definition.meta, parameters: properties }
}

export function createDestinations({ manifest, google, meta, dataLayer, storage, now = () => Date.now() }) {
  const key = 'mymalls_destinations_v1'
  const ttl = 24 * 60 * 60 * 1000
  let seen = new Map(), pending = new Map(), config = {}
  try {
    const saved = JSON.parse(storage?.getItem(key) || '[]')
    if (Array.isArray(saved)) seen = new Map(saved.filter(pair => Array.isArray(pair) && typeof pair[0] === 'string' &&
      typeof pair[1] === 'number' && now() - pair[1] < ttl).slice(-2000))
  } catch (_) { /* In-memory deduplication remains available. */ }
  const mark = id => {
    seen.set(id, now())
    seen = new Map([...seen].filter(([, at]) => now() - at < ttl).slice(-2000))
    try { storage?.setItem(key, JSON.stringify([...seen])) } catch (_) { /* Storage is optional. */ }
  }
  function deliver(event, eventConfig = config) {
    const definition = manifest.events[event.event_name]
    if (definition.ga4 && config.ga4_enabled && !seen.has('ga4:' + event.event_id)) {
      const params = { ...event.properties, page_location: eventConfig.public_screen_url, page_title: eventConfig.screen_name,
        page_referrer: '', send_to: eventConfig.ga4_id, event_id: event.event_id }
      delete params.payment_record_id
      if (event.event_name === 'purchase') {
        params.transaction_id = event.event_id
        params.items = [{ item_id: 'mymalls_' + event.properties.order_type, item_name: 'MyMalls checkout service',
          price: event.properties.value, quantity: 1 }]
      }
      try { if (google(event.event_name, params)) mark('ga4:' + event.event_id) } catch (_) { /* Other channels continue. */ }
    }
    if (definition.meta && config.meta_enabled && !seen.has('meta:' + event.event_id)) {
      try { if (meta(metaMessage(manifest, event), eventConfig)) mark('meta:' + event.event_id) } catch (_) { /* Other channels continue. */ }
    }
    return (!definition.ga4 || !config.ga4_enabled || seen.has('ga4:' + event.event_id)) &&
      (!definition.meta || !config.meta_enabled || seen.has('meta:' + event.event_id))
  }
  return {
    configure(next) {
      config = next || {}
      if (!config.platform_enabled) pending.clear()
    },
    emit(event, next = config) {
      this.configure(next)
      const properties = safeProperties(manifest, event.event_name, event.properties, event.source || 'browser')
      if (!config.platform_enabled || !eventUUID.test(event.event_id) || !properties) return false
      const definition = manifest.events[event.event_name]
      const context = Object.fromEntries(Object.entries({ ...(config.context || {}), screen_name: config.screen_name })
        .filter(([name, value]) => value != null && definition.parameters.includes(name)))
      event = { ...event, properties: { ...context, ...properties } }
      if (!safeProperties(manifest, event.event_name, event.properties, event.source || 'browser')) return false
      const layerKey = 'layer:' + event.event_id
      if (!seen.has(layerKey) && (definition.ga4 || definition.meta)) {
        try { dataLayer({ event: 'mymalls_measurement', measurement: { event_name: event.event_name, event_id: event.event_id,
          properties: event.properties, ga4_owner: definition.ga4_owner, meta_name: definition.meta } }); mark(layerKey) } catch (_) { /* GTM is optional. */ }
      }
      if (!deliver(event)) pending.set(event.event_id, { event, config: { ...config }, at: now() })
      if (pending.size > 400) pending.delete(pending.keys().next().value)
      return true
    },
    flush() {
      if (!config.platform_enabled) return
      for (const [id, entry] of pending) {
        if (now() - entry.at >= ttl || deliver(entry.event, entry.config)) pending.delete(id)
      }
    }
  }
}

// Pixel executes in an opaque-origin sandbox at a neutral URL. Its SDK cannot
// read the parent document, checkout token, query parameters or entered values.
export function createPixelBridge({ window, document, onReady = () => {} }) {
  let frame, ready = false, pixelID
  window.addEventListener('message', event => {
    if (event.source === frame?.contentWindow && event.origin === 'null' && event.data?.type === 'mymalls_pixel_ready') {
      ready = true; onReady()
    }
  })
  return {
    send(message, config) {
      if (!message || !/^\d+$/.test(config.meta_pixel_id || '')) return false
      if (!frame || pixelID !== config.meta_pixel_id) {
        frame?.remove(); ready = false; pixelID = config.meta_pixel_id
        frame = document.createElement('iframe')
        frame.hidden = true; frame.setAttribute('aria-hidden', 'true')
        frame.setAttribute('sandbox', 'allow-scripts')
        frame.referrerPolicy = 'no-referrer'; frame.src = config.pixel_endpoint
        frame.addEventListener('load', () => frame.contentWindow.postMessage({ type: 'mymalls_pixel_ping' }, '*'))
        document.body.appendChild(frame)
      }
      if (!ready) return false
      frame.contentWindow.postMessage({ type: 'mymalls_pixel_event', event: message }, '*')
      return true
    }
  }
}
